import { useQuery } from '@tanstack/react-query';

import { questionnaireDetailDtoSchema } from '@/features/questionnaires/schemas/questionnaireSchemas';
import { apiClient } from '@/lib/api/client';
import { publicApiClient } from '@/lib/api/publicClient';
import { useTenantStore } from '@/stores/tenantStore';

import { usePublicRun } from '../publicRun';

/**
 * The questions a Questionnaire step asks.
 *
 * A SEPARATE CALL, not part of the session payload. The session detail is polled every few seconds
 * while an async step waits on its webhook, and a questionnaire with several sections is larger
 * than everything else in that response put together — folding it in would re-send the whole form
 * on every poll to render a screen the applicant reaches once.
 *
 * Works in both the authenticated run and the anonymous shared-link run, mirroring how
 * `useReferencePhoto` and `useVerifyStep` choose their client and path.
 *
 * `staleTime: Infinity` — the form comes from the run's FROZEN workflow snapshot, so it cannot
 * change for the life of the session. Refetching it would be guaranteed-identical bytes, and
 * re-rendering mid-answer risks disturbing a form the applicant is typing into.
 */
export function useSessionQuestionnaire(
  sessionId: string | undefined,
  stepId: string,
  enabled: boolean,
) {
  const tenantId = useTenantStore((s) => s.tenantId);
  const publicRun = usePublicRun();
  const token = publicRun?.token;

  return useQuery({
    queryKey: ['session-questionnaire', token ?? tenantId ?? '', sessionId ?? '', stepId],
    queryFn: async ({ signal }) => {
      const path = token
        ? `api/v1/public/sessions/${token}/steps/${stepId}/questionnaire`
        : `api/v1/t/${tenantId}/sessions/${sessionId}/steps/${stepId}/questionnaire`;
      const client = token ? publicApiClient : apiClient;
      const raw = await client.get(path, { signal }).json();
      return questionnaireDetailDtoSchema.parse(raw);
    },
    enabled: enabled && (!!token || (!!tenantId && !!sessionId)),
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });
}
